import {
  type AwsMarketplaceAccountModel,
  ContractModel,
  type CustomerModel,
  MarketplaceModel,
  type NavigatorSKUs,
  type SolveSKUs,
  TierModel,
  TierPackageTypes,
} from "@doitintl/cmp-models";
import { getCollection, type ModelReference } from "@doitintl/models-any";
import { type AxiosInstance } from "axios";

import { type TierWithRef } from "../../../Context/TierProvider";
import { isCustomerInPresentationMode } from "../../../Context/useCustomerOrPresentationModeCustomer";
import { consoleErrorWithSentry } from "../../../utils";

export const getAWSMarketplaceData = async (
  awsMpSubscriptionDocId: string
): Promise<AwsMarketplaceAccountModel | undefined> => {
  const awsData = await getCollection(MarketplaceModel)
    .doc("aws-marketplace")
    .collection("awsMarketplaceAccounts")
    .doc(awsMpSubscriptionDocId)
    .get();

  return awsData.data();
};

export const getAWSMarketplaceTiersData = async (
  skuList: Array<NavigatorSKUs | SolveSKUs>
): Promise<TierWithRef[] | undefined> => {
  // TODO: use the useTiers hooks and filter by sku
  const tiersQuerySnapshot = await getCollection(TierModel).where("sku", "in", skuList).get();
  if (tiersQuerySnapshot.empty) {
    return;
  }

  return tiersQuerySnapshot.docs.map((t) => ({
    ...t.asModelData(),
    ref: t.ref,
  }));
};

export const updateAwsMarketplaceAccount = async ({
  customerRef,
  tiers,
}: {
  customerRef: ModelReference<CustomerModel>;
  tiers: TierWithRef[];
}): Promise<void> => {
  const customerTiersMap = tiers.reduce(
    (acc, t) => ({
      ...acc,
      [`tiers.${t.packageType}.tier`]: t.ref,
    }),
    {}
  );
  await customerRef.update({
    marketplace: {
      AWS: {
        accountExists: true,
      },
    },
    ...customerTiersMap,
  });
};

export const enrichAwsMarketplaceEntitlementsWithContracts = async ({
  awsMpSubscriptionDocId,
  customerRef,
}: {
  awsMpSubscriptionDocId: string;
  customerRef: ModelReference<CustomerModel>;
}) => {
  const contractRefs = await getCollection(ContractModel)
    .where("pointOfSale", "==", "aws-marketplace")
    .where("customer", "==", customerRef)
    .get();

  const skuToContract = {};
  for (const t of contractRefs.docs) {
    const d = t.data();
    const tier = await d.tier?.get();
    const sku = tier?.data()?.sku;

    if (sku) {
      skuToContract[sku] = t.ref.id;
    }
  }

  await getCollection(MarketplaceModel)
    .doc("aws-marketplace")
    .collection("awsMarketplaceAccounts")
    .doc(awsMpSubscriptionDocId)
    .update({
      skuToContract: skuToContract as Record<NavigatorSKUs | SolveSKUs, string>,
    });
};

export async function turnOffPresentationModeIfNeeded({
  api,
  customerRef,
  tiers,
}: {
  api: AxiosInstance;
  customerRef: ModelReference<CustomerModel>;
  tiers: TierWithRef[];
}) {
  const customer = (await customerRef.get())?.asModelData();
  if (customer && isCustomerInPresentationMode({ ...customer, id: customerRef.id, ref: customerRef })) {
    try {
      await api.patch(`/v1/customers/${customerRef.id}/presentation-mode/turn-off`, {
        navigatorTierId: tiers?.find((t) => t.packageType === TierPackageTypes.NAVIGATOR)?.ref.id,
        solveTierId: tiers?.find((t) => t.packageType === TierPackageTypes.SOLVE)?.ref.id,
      });
    } catch (e) {
      consoleErrorWithSentry(e);
    }
  }
}
